import { useApp } from 'contexts/app';
import { useAppBar } from 'contexts/appBar';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useNavigationGuard } from 'contexts/navigationGuard';
import { useSnackbar } from 'contexts/snackbar';
import useBeforeUnload from 'hooks/useBeforeUnload';
import useFormValidation from 'hooks/useFormValidation';
import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import rubricService from 'services/rubric';
import { Button, Stack } from 'shell/components';
import { DESCRIPTION_WORD_COUNT_LIMIT, RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT } from 'utils/constants';
import {
    getHTMLWordCount,
    getRubric,
    getWordCount,
    isValidRate,
    roundCriteriaOptionRates,
    sortCriteria,
} from 'utils/helper';
import { v4 as uuid } from 'uuid';

const PageContext = createContext();

const PageProvider = (props) => {
    const { setNode } = useAppBar();
    const { course, setRubrics } = useApp();
    const { auth } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const { onBeforeNavigate } = useNavigationGuard();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');

    const isCancelled = useRef(false);
    const [form, setForm] = useState({
        title: '',
        description: '',
        numCriteria: '2',
        useCustomCriteria: false,
        criteria: [
            {
                id: uuid(),
                title: '',
                description: '',
            },
            {
                id: uuid(),
                title: '',
                description: '',
            },
        ],
        maxScore: '',
        numOptions: '2',
    });
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [rubricErrors, setRubricErrors] = useState({
        title: '',
        criteria: {},
    });
    const [hasClickedSubmitRubric, setHasClickedSubmitRubric] = useState(false);

    const getErrorMessage = useCallback(
        (key, value, form) => {
            switch (key) {
                case 'title':
                    return !value.trim() ? t('error.empty-assignment-title', 'Please enter an assignment title') : '';
                case 'description':
                    if (!getHTMLWordCount(value))
                        return t('error.empty-assignment-description', 'Please enter an assignment description');
                    if (getHTMLWordCount(value) > DESCRIPTION_WORD_COUNT_LIMIT)
                        return t('error.description-too-long', 'Description is too long');
                    return '';
                case 'criteria':
                    if (!form.useCustomCriteria) return undefined;
                    return form.criteria.map((criterion) => ({
                        title: !criterion.title.trim()
                            ? t('rubric.create-ai-rubric.error.empty-criterion', 'Please enter a criterion name')
                            : '',
                    }));
                case 'maxScore':
                    if (!value.trim() || !isValidRate(value)) return t('error.empty-number', 'Please enter a number');
                    return '';
                default:
                    break;
            }
        },
        [t]
    );

    const { errors, validate, validateAll } = useFormValidation(form, getErrorMessage);

    const onChange = (key, value) => {
        if (key === 'useCustomCriteria' && value === false) {
            setDialog({
                open: true,
                message: t('dialog.exit-rubric-creation.title', 'Existing Criteria Will Be Deleted'),
                description: t(
                    'dialog.exit-rubric-creation.description',
                    'If you deselect the option to specify your own criteria, any criteria you have already created will be permanently deleted. Are you sure you want to continue?'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.continue', 'Continue'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    setForm((current) => ({
                        ...current,
                        useCustomCriteria: false,
                        criteria: [
                            {
                                id: uuid(),
                                title: '',
                                description: '',
                            },
                            {
                                id: uuid(),
                                title: '',
                                description: '',
                            },
                        ],
                    }));
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        } else {
            setForm((current) => {
                const newForm = { ...current, [key]: value };

                if (key === 'numCriteria') {
                    newForm.criteria = Array.from(Array(Number(value))).map(() => ({
                        id: uuid(),
                        title: '',
                        description: '',
                    }));
                }

                if (hasClickedSubmit) {
                    validate(key, value, newForm);
                }

                return newForm;
            });
        }
    };

    const onClickAddCriterion = () => {
        setForm((current) => ({
            ...current,
            criteria: [
                ...current.criteria,
                {
                    id: uuid(),
                    title: '',
                    description: '',
                },
            ],
        }));
    };

    const onClickDeleteCriterion = (criterion) => {
        setDialog({
            open: true,
            message: t('dialog.delete-criterion.title', 'Delete Criterion?'),
            description: t(
                'dialog.delete-criterion.description',
                'Deleting this criterion is permanent and cannot be undone.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.delete', 'Delete'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                setForm((current) => ({
                    ...current,
                    criteria: current.criteria.filter(({ id }) => id !== criterion.id),
                }));
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const onClickBack = () => {
        setDialog({
            open: true,
            message: t('dialog.return-to-settings.title', 'Returning to Settings Will Interrupt Rubric Generation'),
            description: t(
                'dialog.return-to-settings.description',
                'If you proceed, the rubric will not be generated. Do you want to continue?'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.continue', 'Continue'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                setIsLoading(false);
                isCancelled.current = true;
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const validateTitle = useCallback(() => {
        const error = result.title.trim() ? '' : t('error.empty-name', 'Please enter a name');
        setRubricErrors((current) => {
            return { ...current, title: error };
        });
        return !Boolean(error);
    }, [result?.title, t]);

    const validateCriteria = useCallback(() => {
        let error = false;
        result.criteria.forEach((criterion) => {
            criterion.options.forEach((option) => {
                const isRateInvalid = !isValidRate(option.rate);
                const isDescriptionInvalid =
                    getWordCount(option.description) > RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT;
                const hasOptionError = isRateInvalid || isDescriptionInvalid;
                if (hasOptionError) error = true;
                setRubricErrors((current) => ({
                    ...current,
                    criteria: {
                        ...(current?.criteria || {}),
                        [criterion.id]: {
                            ...(current?.criteria?.[criterion.id] || {}),
                            [option.id]: {
                                rate: isRateInvalid ? t('error.empty-number', 'Please enter a number') : '',
                                description: isDescriptionInvalid
                                    ? t('error.word-count-exceeded', 'Word count exceeded')
                                    : '',
                            },
                        },
                    },
                }));
            });
        });
        return !Boolean(error);
    }, [result?.criteria, t]);

    const onClickEditSettings = () => {
        setDialog({
            open: true,
            message: t('dialog.editing-settings-warning.title', 'Editing Settings Will Erase This Rubric'),
            description: t(
                'dialog.editing-settings-warning.description',
                'If you continue, you will need to generate a new rubric.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.continue', 'Continue'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                setResult(null);
                setRubricErrors({ title: '', criteria: {} });
                setHasClickedSubmitRubric(false);
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const onClickGenerate = useCallback(async () => {
        isCancelled.current = false;
        setHasClickedSubmit(true);
        const isValid = validateAll();
        if (isValid) {
            setIsLoading(true);
            const response = await getRubric({ ...form, language: course?.language });
            const data = await response.json();
            if (isCancelled.current) return;
            try {
                const { result } = data;
                setResult({
                    title: result.title,
                    criteria: result.criteria.map((criterion) => {
                        return {
                            ...criterion,
                            id: uuid(),
                            options: criterion.options
                                .sort((a, b) => {
                                    const numA = Number(a.rate);
                                    const numB = Number(b.rate);
                                    if (a.rate === '') return 1;
                                    if (b.rate === '') return -1;
                                    if (!numA && !numB) return 0;
                                    return numB - numA;
                                })
                                .map((option) => ({ ...option, id: uuid(), rate: option.rate.toString() })),
                        };
                    }),
                    sort: 'desc',
                });
            } catch (e) {
                message.error(
                    t(
                        'rubric.create-ai-rubric.error.rubric-generation-failed',
                        'Rubric generation failed, please try again.'
                    )
                );
            } finally {
                setIsLoading(false);
            }
        } else {
            message.error(t('error.invalid-fields', 'One or more fields are not valid.'));
        }
    }, [course?.language, form, message, validateAll, t]);

    const onClickCreate = useCallback(() => {
        setHasClickedSubmitRubric(true);
        const hasAtLeastOneCriterion = result.criteria.length > 0;
        const isValid = validateTitle() && validateCriteria() && hasAtLeastOneCriterion;
        if (isValid) {
            setDialog({
                open: true,
                message: t('dialog.save-rubric-warning.title', 'Did You Verify All AI-Generated Information?'),
                description: t(
                    'dialog.save-rubric-warning.description',
                    'Before proceeding, please confirm that you have reviewed and verified all AI-generated information for accuracy and completeness.'
                ),
                note: '',
                variant: 'primary',
                actionButtonText: t('common:controls.confirm', 'Confirm'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    const timestamp = new Date().getTime();
                    const criteriaRoundedOptionRates = roundCriteriaOptionRates(result.criteria);
                    const sortedCriteria = sortCriteria(criteriaRoundedOptionRates, result.sort);
                    const rubric = {
                        id: uuid(),
                        active: true,
                        created_on: timestamp,
                        modified_on: timestamp,
                        rubric: { ...result, criteria: sortedCriteria },
                        user_id: auth.user_id,
                    };
                    setRubrics((current) => [...current, rubric]);
                    rubricService.createRubric(rubric);
                    history.push('/rubrics');
                    message.success(t('message.rubric-saved-success', 'Rubric was saved successfully.'));
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        } else {
            message.error(t('error.invalid-rubric', 'The rubric is not valid.'));
        }
    }, [
        auth.user_id,
        closeDialog,
        history,
        message,
        result,
        setDialog,
        setRubrics,
        t,
        validateCriteria,
        validateTitle,
    ]);

    useEffect(() => {
        if (isLoading) return;

        if (result) {
            setNode(
                <Stack alignItems={'center'} direction={'row'} justifyContent={'flex-end'} spacing={4}>
                    <Button
                        color={'neutral'}
                        variant={'tertiary'}
                        onClick={() => {
                            setDialog({
                                open: true,
                                message: t(
                                    'dialog.return-rubric-generation-settings.title',
                                    'Return to Rubric Generation Settings?'
                                ),
                                description: t(
                                    'dialog.return-rubric-generation-settings.description',
                                    'If you exit, this generated rubric will be lost.'
                                ),
                                note: '',
                                variant: 'destructive',
                                actionButtonText: t('common:controls.exit', 'Exit'),
                                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                                onAction: () => {
                                    setResult(null);
                                    setRubricErrors({ title: '', criteria: {} });
                                    setHasClickedSubmitRubric(false);
                                    closeDialog();
                                },
                                onCancel: () => closeDialog(),
                                onClose: () => closeDialog(),
                            });
                        }}
                    >
                        {t('common:controls.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={onClickCreate}>{t('rubric.create-rubric', 'Create Rubric')}</Button>
                </Stack>
            );
        } else {
            setNode(
                <Stack alignItems={'center'} direction={'row'} justifyContent={'flex-end'} spacing={4}>
                    <Button color={'neutral'} variant={'tertiary'} onClick={() => history.push('/rubrics/get-started')}>
                        {t('common:controls.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={onClickGenerate}>{t('rubric.generate-rubric', 'Generate Rubric')}</Button>
                </Stack>
            );
        }

        return () => setNode(null);
    }, [closeDialog, history, isLoading, onClickCreate, onClickGenerate, result, setDialog, setNode, t]);

    useEffect(() => {
        if (isLoading) {
            onBeforeNavigate((navigate) => {
                setDialog({
                    open: true,
                    message: t(
                        'dialog.navigating-away-rubric-generation.title',
                        'Navigating Away Will Interrupt Rubric Generation'
                    ),
                    description: t(
                        'dialog.navigating-away-rubric-generation.description',
                        'If you proceed, the rubric will not be generated. Do you want to continue?'
                    ),
                    note: '',
                    variant: 'destructive',
                    actionButtonText: t('common:controls.continue', 'Continue'),
                    cancelButtonText: t('common:controls.cancel', 'Cancel'),
                    onAction: () => {
                        closeDialog();
                        navigate();
                    },
                    onCancel: () => closeDialog(),
                    onClose: () => closeDialog(),
                });
            });
        }

        if (result) {
            onBeforeNavigate((navigate) => {
                setDialog({
                    open: true,
                    message: t('dialog.discard-rubric.title', 'Discard Rubric?'),
                    description: t(
                        'dialog.discard-rubric.description',
                        'If you exit, this generated rubric will be lost.'
                    ),
                    note: '',
                    variant: 'destructive',
                    actionButtonText: t('common:controls.continue', 'Continue'),
                    cancelButtonText: t('common:controls.cancel', 'Cancel'),
                    onAction: () => {
                        closeDialog();
                        navigate();
                    },
                    onCancel: () => closeDialog(),
                    onClose: () => closeDialog(),
                });
            });
        }

        return () => {
            onBeforeNavigate(null);
        };
    }, [closeDialog, isLoading, onBeforeNavigate, result, setDialog, t]);

    useBeforeUnload();

    return (
        <PageContext.Provider
            value={{
                form,
                errors,
                onChange,
                onClickAddCriterion,
                onClickDeleteCriterion,

                isLoading,
                onClickBack,

                result,
                setResult,
                rubricErrors,
                setRubricErrors,
                hasClickedSubmitRubric,
                setHasClickedSubmitRubric,
                validateTitle,
                validateCriteria,
                onClickEditSettings,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
